import { useEffect, useMemo, useState } from "react";
import { FolderOpen, Monitor, User } from "lucide-react";
import { Card, CardContent } from "../../ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../ui/tabs";
import { TopLolBinComputerData, TopLolBinData, TopLolBinUserData } from "../interfaces/CommonInterfaces";

type TabKey = "lolbins" | "users" | "computers";

interface LolbinTopTabsProps {
    topUsers?: TopLolBinUserData[];
    topLolBin?: TopLolBinData[];
    topComputers?: TopLolBinComputerData[];
    loading: boolean;
    showTopLolBins?: boolean;
    showTopUsers?: boolean;
    showTopComputers?: boolean;
}

export default function LolbinTopUserLbs({
    topUsers = [],
    topLolBin = [],
    topComputers = [],
    loading,
    showTopLolBins = true,
    showTopUsers = true,
    showTopComputers = true,
}: LolbinTopTabsProps) {
    const [activeTab, setActiveTab] = useState<TabKey>("lolbins");

    const sortedTopLolBin = useMemo(
        () => [...topLolBin].sort((a, b) => b.percentage - a.percentage),
        [topLolBin]
    );

    const sortedTopUsers = useMemo(
        () => [...topUsers].sort((a, b) => b.percentage - a.percentage),
        [topUsers]
    );

    const sortedTopComputers = useMemo(
        () => [...topComputers].sort((a, b) => b.percentage - a.percentage),
        [topComputers]
    );

    const availableTabs = useMemo(() => {
        const tabs: { value: TabKey; label: string }[] = [];
        if (showTopLolBins) tabs.push({ value: "lolbins", label: "Top LOLBins" });
        if (showTopUsers) tabs.push({ value: "users", label: "Top Users" });
        if (showTopComputers) tabs.push({ value: "computers", label: "Top Computers" });
        return tabs;
    }, [showTopComputers, showTopLolBins, showTopUsers]);

    useEffect(() => {
        const hasActiveTab = availableTabs.some((tab) => tab.value === activeTab);
        if (!hasActiveTab && availableTabs.length > 0) {
            setActiveTab(availableTabs[0].value);
        }
    }, [activeTab, availableTabs]);

    if (availableTabs.length === 0) {
        return null;
    }

    const renderSkeleton = () => (
        <div className="space-y-3">
            {[...Array(5)].map((_, idx) => (
                <div key={idx} className="space-y-1 animate-pulse">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center flex-1 gap-2">
                            <div className="w-3 h-3 bg-gray-300 rounded-full" />
                            <div className="w-32 h-3 bg-gray-300 rounded" />
                        </div>
                        <div className="w-8 h-3 bg-gray-300 rounded" />
                    </div>
                    <div className="w-full bg-muted rounded-full h-1.5">
                        <div className="bg-gray-300 h-1.5 rounded-full w-1/2" />
                    </div>
                </div>
            ))}
        </div>
    );

    const renderNoData = (label: string) => (
        <div className="py-8 text-sm text-center text-muted-foreground">
            No {label} data found.
        </div>
    );

    return (
        <Card>
            <CardContent className="p-0">
                <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as TabKey)}>
                    <TabsList
                        className="grid w-full"
                        style={{ gridTemplateColumns: `repeat(${availableTabs.length}, minmax(0, 1fr))` }}
                    >
                        {availableTabs.map((tab) => (
                            <TabsTrigger
                                key={tab.value}
                                value={tab.value}
                                className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:font-medium"
                            >
                                {tab.label}
                            </TabsTrigger>
                        ))}
                    </TabsList>

                    {showTopLolBins && (
                        <TabsContent value="lolbins" className="p-4">
                            {loading && renderSkeleton()}
                            {!loading && topLolBin.length === 0 && renderNoData("LOLBin")}
                            {!loading && topLolBin.length > 0 && (
                                <div className="space-y-3">
                                    {sortedTopLolBin.map((lolbin) => (
                                        <div key={lolbin.id} className="space-y-1">
                                            <div className="flex items-start justify-between gap-3">
                                                <div className="flex min-w-0 flex-1 items-start gap-2">
                                                    <FolderOpen className="mt-0.5 h-3 w-3 shrink-0 text-muted-foreground" />
                                                    <span className="min-w-0 break-all whitespace-normal font-mono text-xs">
                                                        {lolbin.executable}
                                                    </span>
                                                </div>
                                                <span className="w-12 shrink-0 text-right text-xs font-medium">
                                                    {lolbin.percentage.toFixed(2)}%
                                                </span>
                                            </div>
                                            <div className="w-full bg-muted rounded-full h-1.5">
                                                <div
                                                    className="bg-primary h-1.5 rounded-full transition-all duration-300"
                                                    style={{ width: `${lolbin.percentage}%` }}
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </TabsContent>
                    )}

                    {showTopUsers && (
                        <TabsContent value="users" className="p-4">
                            {loading && renderSkeleton()}
                            {!loading && topUsers.length === 0 && renderNoData("user")}
                            {!loading && topUsers.length > 0 && (
                                <div className="space-y-3">
                                    {sortedTopUsers.map((user) => (
                                        <div key={user.id} className="space-y-1">
                                            <div className="flex items-start justify-between gap-3">
                                                <div className="flex min-w-0 flex-1 items-start gap-2">
                                                    <User className="mt-0.5 h-3 w-3 shrink-0 text-muted-foreground" />
                                                    <span className="min-w-0 break-all whitespace-normal text-xs">
                                                        {user.username}
                                                    </span>
                                                </div>
                                                <span className="w-12 shrink-0 text-right text-xs font-medium">
                                                    {user.percentage.toFixed(2)}%
                                                </span>
                                            </div>
                                            <div className="w-full bg-muted rounded-full h-1.5">
                                                <div
                                                    className="bg-primary h-1.5 rounded-full transition-all duration-300"
                                                    style={{ width: `${user.percentage}%` }}
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </TabsContent>
                    )}

                    {showTopComputers && (
                        <TabsContent value="computers" className="p-4">
                            {loading && renderSkeleton()}
                            {!loading && topComputers.length === 0 && renderNoData("computer")}
                            {!loading && topComputers.length > 0 && (
                                <div className="space-y-3">
                                    {sortedTopComputers.map((computer) => (
                                        <div key={computer.id} className="space-y-1">
                                            <div className="flex items-start justify-between gap-3">
                                                <div className="flex min-w-0 flex-1 items-start gap-2">
                                                    <Monitor className="mt-0.5 h-3 w-3 shrink-0 text-muted-foreground" />
                                                    <span className="min-w-0 break-all whitespace-normal text-xs">
                                                        {computer.computerName}
                                                    </span>
                                                </div>
                                                <span className="w-12 shrink-0 text-right text-xs font-medium">
                                                    {computer.percentage.toFixed(2)}%
                                                </span>
                                            </div>
                                            <div className="w-full bg-muted rounded-full h-1.5">
                                                <div
                                                    className="bg-primary h-1.5 rounded-full transition-all duration-300"
                                                    style={{ width: `${computer.percentage}%` }}
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </TabsContent>
                    )}
                </Tabs>
            </CardContent>
        </Card>
    );
}
